import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, Link, ScrollRestoration, useLocation } from "react-router-dom";
import logo from "../images/logo-full.svg";
import logoMobile from "../images/logo-header.svg";
import { useAppContext } from "../utils/context/app-context";
import { AlertMessage, MaterialIcon, LoadingSpinner } from "../components/UiComponents";
import { Notifications } from "../components/Notifications";
import {
	CourseDetailItem,
	CourseDetailHeader,
} from "../components/CourseComponents";
import { parse } from "date-fns";
import { Modal } from "react-bootstrap";

const MainLayout = () => {
	const { state, api } = useAppContext();
	const [showCourseDetails, setShowCourseDetails] = useState(true);
	const [showSubHeader, setShowSubHeader] = useState(true);
	const [showback, setShowBack] = useState(false);
	const current_course = api.getCurrentCourse();
	const location = useLocation();

	useEffect(() => {
		if (document.body.classList.contains("minimal-layout")) {
			document.body.classList.remove("minimal-layout");
		}
	}, []);

	// If current route is /, set showCourseDetails to false, else true
	useEffect(() => {
		if (location.pathname === "/") {
			setShowCourseDetails(false);
			setShowBack(false);
			setShowSubHeader(true)
		} else if (location.pathname === "/upcoming-courses" || location.pathname === "/teaching-logs") {
			setShowCourseDetails(false);
			setShowBack(true);
			setShowSubHeader(false)
		} else {
			setShowCourseDetails(true);
			setShowBack(true);
			setShowSubHeader(false)
		}
	}, [location.pathname]);

	return (
		<div className="main-layout">
			<ScrollRestoration />
			<Header
				state={state}
				course={current_course}
				showCourseDetails={showCourseDetails}
				showSubHeader={showSubHeader}
				showBack={showback}
			/>
			<div className="container-fluid">
				<div className="main-layout-body">
					<Outlet />
				</div>
			</div>
			<Footer />
		</div>
	);
};

const Header = ({
	state = null,
	course = null,
	showCourseDetails,
	showSubHeader,
	showBack = false,
}) => {
	const [showDateWarning, setShowDateWarning] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const navigate = useNavigate();
	const { api } = useAppContext();
	const current_date = api.getCurrentDate();
	const current_course = api.getCurrentCourse();

	useEffect(() => {
		checkDateIsCorrect();
		// console.log('here')
	}, [current_date, current_course]);

	function checkDateIsCorrect() {
		setShowDateWarning(false);
		if (current_date !== null) {
			// Check if the date is today, if not, check if today exists in course.dates
			const today = new Date();
			const date = parse(current_date.date, "yyyy-MM-dd", new Date());
			// Compare date string to check if the same
			if (date.toDateString() != today.toDateString()) {
				// Check if today exists in course.dates
				const todayExists = current_course.dates.find((d) => {
					const d_date = parse(d.date, "yyyy-MM-dd", new Date());
					return d_date.toDateString() == today.toDateString();
				});

				if (todayExists) {
					setShowDateWarning(true);
				}
			}
		}
	}

	function handleChangeDate() {
		// Find today's date in course.dates and set it as current_date
		const today = new Date();
		const todayExists = current_course.dates.find((d) => {
			const d_date = parse(d.date, "yyyy-MM-dd", new Date());
			return d_date.toDateString() == today.toDateString();
		});
		if (todayExists) {
			api.changeDate(todayExists);
			setShowDateWarning(false);
		}
	}

	return (
		<>
			{state.offline && (
				<div className="offline-message">
					<AlertMessage
						type="danger"
						message={
							<>
								<div>You are currently offline, Course data may be outdated and functionality will be limited.</div>
							</>
						}
						classes={'alert-banner--no-radius alert-banner--centre'}
					/>
				</div>
			)}
			<header className="header">
				<div className="container-fluid">
					<div className="header-top">
						<div className="header-top__logo">
							<Link to="/">
								<img src={logo} className="header-top__logo-desktop" alt="Logo" />
								<img src={logoMobile} className="header-top__logo-mobile" alt="Logo" />
							</Link>
						</div>
						<div className="header-top__mobile-buttons">
							<div className="header-top__mobile-notifications">
								<Notifications notifications={state?.notifications} />
							</div>
							<button className="header-mobile-menu-button" onClick={() => setShowMobileMenu(true)}>
								<MaterialIcon icon="menu" />
							</button>
						</div>
						<div className="header-top__details">
							<div className="header-top__name">
								{state?.user?.first_name} {state?.user?.last_name}
							</div>
							<div className="header-top__logout-link">
								<a href="#" onClick={(e) => { e.preventDefault(); if (window.confirm("Are you sure you want to clear your offline course cache? This will reload the app.")) api.clearCache(); }}>Clear Cache</a>
								<span style={{ margin: "0 8px" }}>|</span>
								<Link to="/logout">Logout</Link>
							</div>
						</div>
					</div>
					<div className="header-bottom">
						<div className="header-bottom__left">
							{showCourseDetails ? (
								<>
									{state.loading_courses ? (
										<div className="course-details-loading-container">
											<LoadingSpinner text={"Loading Course Data..."} />
										</div>
									) : (
										<CourseDetailHeader course={course} />
									)}
								</>
							) : (
								<>
									<div className="header-bottom__name">
										Hey {state?.user?.first_name}
									</div>
									{showSubHeader && (
										<div className="header-bottom__text">
											Welcome. Please select a course below to continue.
										</div>
									)}
								</>
							)}
						</div>
						<div className="header__notifications">
							<Notifications />
						</div>
					</div>
				</div>
			</header>
			<MobileMenu show={showMobileMenu} hide={() => setShowMobileMenu(false)} />
			{showDateWarning && (
				<div className="container-fluid">
					<div className="date-warning mt-2" onClick={handleChangeDate}>
						<AlertMessage
							type="danger"
							message={
								<>
									<div>
										Please click here to select the correct date for
										today's session.
									</div>
								</>
							}
							icon={<MaterialIcon icon="warning" />}
						/>
					</div>
				</div>
			)}
			{showBack && (
				<div className="header-back-container">
					<div className="container-fluid">
						<Link
							to=".."
							onClick={(e) => {
								e.preventDefault();
								navigate(-1);
							}}
							className="header-back">
							<MaterialIcon icon="arrow_back_ios_new" /> Go Back
						</Link>
						{course?.id && (
							<Link
								to={`/courses/${course.id}`}
								className="header-all-courses"
								style={{ marginRight: '20px' }}>
								<MaterialIcon icon="home" /> Course Dashboard
							</Link>
						)}
						<Link
							to="/"
							className="header-all-courses">
							<MaterialIcon icon="dashboard" /> View All Courses
						</Link>
					</div>
				</div>
			)}
		</>
	);
};

const MobileMenu = ({ show, hide }) => {
	const { state, api } = useAppContext();
	return (
		<>
			<div className={`mobile-menu ${show ? 'mobile-menu--visible' : ''}`}>
				<div className="mobile-menu__inner">
					<div className="mobile-menu__top">
						<div className="mobile-menu__logo">
							<img src={logoMobile} className="mobile-menu__logo-mobile" alt="Logo" />
						</div>
						<div className="mobile-menu__close" onClick={hide}>
							<MaterialIcon icon="close" />
						</div>
					</div>
					<ul className="mobile-menu__items">
						<li>
							<Link to="/">Courses</Link>
						</li>
					</ul>
					<div className="mobile-menu__bottom">
						<div className="mobile-menu__name">
							{state?.user?.first_name} {state?.user?.last_name}
						</div>
						<div className="mobile-menu__logout">
							<a href="#" style={{ display: 'block', marginBottom: '8px' }} onClick={(e) => { e.preventDefault(); if (window.confirm("Are you sure you want to clear your offline course cache? This will reload the app.")) api.clearCache(); }}>Clear Cache</a>
							<Link to="/logout">Logout</Link>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

const Footer = () => {
	const yearToday = new Date().getFullYear();
	const { state } = useAppContext();
	const terms = state?.terms_text;
	const privacy = state?.privacy_text;
	const cookies = state?.cookies_text;
	const [showTerms, setShowTerms] = useState(false);
	const [showPrivacy, setShowPrivacy] = useState(false);
	const [showCookies, setShowCookies] = useState(false);

	return (
		<footer className="footer">
			<div className="container-fluid">
				<div className="footer__inner">
					<p className="footer__copyright">
						{yearToday} &copy; PAMOVA Cymru General Services Ltd
					</p>
					<div className="footer__right">
						<ul className="footer__links">
							{terms && (
								<li>
									<a href="#" onClick={() => setShowTerms(true)}>Terms & Conditions</a>
								</li>
							)}
							{privacy && (
								<li>
									<a href="#" onClick={() => setShowPrivacy(true)}>Privacy Policy</a>
								</li>
							)}
							{cookies && (
								<li>
									<a href="#" onClick={() => setShowCookies(true)}>Cookies Policy</a>
								</li>
							)}
						</ul>
					</div>
				</div>
			</div>
			{terms && (
				<Modal
					show={showTerms}
					onHide={() => setShowTerms(false)}
					dialogClassName="modal-terms"
					centered
					size="lg"
				>
					<Modal.Header>
						<Modal.Title>Terms & Conditions</Modal.Title>
						<button type="button" className="close" onClick={() => setShowTerms(false)}>
							<span aria-hidden="true">×</span>
							<span className="sr-only">Close</span>
						</button>
					</Modal.Header>
					<Modal.Body>
						<div className="modal-terms__content" dangerouslySetInnerHTML={{ __html: terms }} />
					</Modal.Body>
					<Modal.Footer>
						<button className="btn btn-secondary" onClick={() => setShowTerms(false)}>
							Close
						</button>
					</Modal.Footer>
				</Modal>
			)}
			{privacy && (
				<Modal
					show={showPrivacy}
					onHide={() => setShowPrivacy(false)}
					dialogClassName="modal-privacy"
					centered
					size="lg"
				>
					<Modal.Header>
						<Modal.Title>Privacy Policy</Modal.Title>
						<button type="button" className="close" onClick={() => setShowPrivacy(false)}>
							<span aria-hidden="true">×</span>
							<span className="sr-only">Close</span>
						</button>
					</Modal.Header>
					<Modal.Body>
						<div className="modal-privacy__content" dangerouslySetInnerHTML={{ __html: privacy }} />
					</Modal.Body>
					<Modal.Footer>
						<button className="btn btn-secondary" onClick={() => setShowPrivacy(false)}>
							Close
						</button>
					</Modal.Footer>
				</Modal>
			)}
			{cookies && (
				<Modal
					show={showCookies}
					onHide={() => setShowCookies(false)}
					dialogClassName="modal-cookies"
					centered
					size="lg"
				>
					<Modal.Header>
						<Modal.Title>Cookies Policy</Modal.Title>
						<button type="button" className="close" onClick={() => setShowCookies(false)}>
							<span aria-hidden="true">×</span>
							<span className="sr-only">Close</span>
						</button>
					</Modal.Header>
					<Modal.Body>
						<div className="modal-cookies__content" dangerouslySetInnerHTML={{ __html: cookies }} />
					</Modal.Body>
					<Modal.Footer>
						<button className="btn btn-secondary" onClick={() => setShowCookies(false)}>
							Close
						</button>
					</Modal.Footer>
				</Modal>
			)}
		</footer>
	);
};

export default MainLayout;
